/**
 * IvyChat — presentation panel (Phase 2D).
 *
 * As of Phase 2D, IvyChat is a "dumb" presentation component rendered INSIDE
 * IvyDock (the portal-mounted container at App root). IvyDock owns session +
 * message state; IvyChat receives it via props. This eliminates the pre-2D
 * dual-instance quirk (overlay + /ivy full-page had separate session IDs).
 *
 * Local state kept here:
 *   - `input` — the user's typing buffer (not interesting to the dock/session)
 *   - `messagesEndRef` — auto-scroll anchor
 *
 * Everything else (sessionId, messages, loading, error, pageContext) is lifted
 * to IvyDock. Network calls (sendIvyMessage, endIvySession) happen there too.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { usePodcast } from '../contexts/PodcastContext'
import { MessageCircle, X, Send, Plus } from 'lucide-react'
import { GraspPodIvyIcon } from './icons/GraspPodIcons'
import IvyDockTypingIndicator from './IvyDockTypingIndicator'

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface IvyChatProps {
  messages: ChatMessage[]
  loading: boolean
  error: string | null
  onSend: (text: string) => void
  onNewSession: () => void
  onClose: () => void
}

export default function IvyChat({
  messages,
  loading,
  error,
  onSend,
  onNewSession,
  onClose,
}: IvyChatProps) {
  const { selectedPodcast } = usePodcast()
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll on new messages / typing indicator appearance
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const handleSend = () => {
    const trimmed = input.trim()
    if (!trimmed || loading || !selectedPodcast) return
    setInput('')
    onSend(trimmed)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const quickActions = [
    { label: 'Morning Brief', prompt: 'Give me a morning brief on my podcast performance' },
    { label: 'Check Trends', prompt: 'What topics are trending in my niche right now?' },
    { label: 'Review Clips', prompt: 'Which clips should I publish next?' },
    { label: 'Health Check', prompt: 'How is my podcast health score looking?' },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-white/[0.06]">
        <div className="w-9 h-9 rounded-full bg-gray-800 border border-[rgba(167,139,250,0.20)] flex items-center justify-center flex-shrink-0">
          <GraspPodIvyIcon size={20} active />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-gray-100">Ivy</h3>
          <p className="text-[10px] text-gray-500">Podcast Intelligence Assistant</p>
        </div>
        <button
          onClick={onNewSession}
          className="p-1.5 rounded-lg hover:bg-white/[0.06] text-gray-400 transition-colors"
          title="New conversation"
          aria-label="Start new conversation"
        >
          <Plus size={16} />
        </button>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-white/[0.06] text-gray-400 transition-colors"
          title="Collapse"
          aria-label="Collapse Ivy dock"
        >
          <X size={16} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B6B]/20 to-[#FFAB76]/20 flex items-center justify-center mx-auto mb-3">
              <MessageCircle size={20} className="text-[#FF6B6B]" />
            </div>
            <p className="text-sm text-gray-400 mb-4">Hi! I&apos;m Ivy, your podcast intelligence assistant.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {quickActions.map((a) => (
                <button
                  key={a.label}
                  onClick={() => { setInput(a.prompt) }}
                  className="text-[11px] font-medium text-[#FF6B6B] bg-[#FF6B6B]/10 px-3 py-1.5 rounded-lg hover:bg-[#FF6B6B]/20 transition-colors"
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : ''}`}>
            {msg.role === 'assistant' && (
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFAB76] flex items-center justify-center flex-shrink-0 mt-0.5">
                <MessageCircle size={12} className="text-white" />
              </div>
            )}
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-gray-700 text-gray-100'
                  : 'bg-white/[0.04] text-gray-300'
              }`}
            >
              <p className="whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-2.5">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FFAB76] flex items-center justify-center flex-shrink-0">
              <MessageCircle size={12} className="text-white" />
            </div>
            <div className="bg-white/[0.04] rounded-2xl px-4 py-3">
              <IvyDockTypingIndicator visible={loading} />
            </div>
          </div>
        )}

        {error && (
          <div className="text-xs text-red-400 bg-red-500/10 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-white/[0.06]">
        <div className="flex items-center gap-2 bg-white/[0.04] rounded-xl px-3 py-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={selectedPodcast ? 'Ask Ivy anything...' : 'Select a podcast first'}
            disabled={!selectedPodcast || loading}
            className="flex-1 bg-transparent text-sm text-gray-200 placeholder-gray-500 focus:outline-none disabled:opacity-50"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading || !selectedPodcast}
            className="p-1.5 rounded-lg bg-gradient-to-r from-[#FF6B6B] to-[#FFAB76] text-white disabled:opacity-30 hover:opacity-90 transition-opacity"
            aria-label="Send message"
          >
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}
